import React, { useState, useMemo, useRef, useEffect } from "react";
import { contactDirectoryApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./ContactDirectory.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const DEPARTMENTS = [
  "Administration", "Admission Cell", "Examination Cell", "Finance Office", 
  "IT Support", "Library", "Placement Cell", "Sports Department", 
  "Student Welfare", "Security", "Other"
];

const TABS = [
  { key: "all",            label: "All Contacts",    icon: "👥" },
  { key: "Administration", label: "Administration",  icon: "🏛️" },
  { key: "Academic",       label: "Academic Depts",  icon: "📚" },
  { key: "Student Services", label: "Student Care",  icon: "🤝" },
  { key: "IT & Infrastructure", label: "Support & IT", icon: "🛠️" },
];

const BLANK_CONTACT = {
  _id: null,
  Full_Name: "",
  Designation: "",
  Department: "Administration",
  Email_Address: "",
  Phone_Number: "",
  Extension: "",
  Office_Location: "",
  Availability: "",
  Status: "Active",
  Notes: "",
  Show_on_Website: true,
  Department_Head: false,
  Primary_Contact: false,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ContactDirectory = () => {
  const [contacts, setContacts]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [activeTab, setActiveTab]   = useState("all");
  const [search, setSearch]         = useState("");
  const [modal, setModal]           = useState(""); // "add" | "edit" | "view"
  const [form, setForm]             = useState(BLANK_CONTACT);
  const [viewItem, setViewItem]     = useState(null);
  
  const [dropdown, setDropdown]     = useState({ id: null, top: 0, left: 0 });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchContacts();
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) closeDD(); };
    const onScroll = () => closeDD();
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await apiClient(contactDirectoryApi);
      const json = await res.json();
      if (res.ok) setContacts(json.data || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    } finally {
      setLoading(false);
    }
  };

  const closeDD = () => setDropdown({ id: null, top: 0, left: 0 });
  const openDD  = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ id, top: rect.bottom + 8, left: rect.right - 210 });
  };

  const openAdd  = () => { setForm({ ...BLANK_CONTACT }); setModal("add"); };
  const openEdit = (item) => { setForm({ ...item }); setModal("edit"); closeDD(); };
  const openView = (item) => { setViewItem(item); setModal("view"); closeDD(); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const isAdd = modal === "add";
      const method = isAdd ? "POST" : "PUT";
      const url = isAdd ? contactDirectoryApi : `${contactDirectoryApi}/${form._id}`;
      const res = await apiClient(url, {
        method,
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchContacts();
        setModal("");
      }
    } catch (err) {
      console.error("Error saving contact:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await apiClient(`${contactDirectoryApi}/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchContacts();
        setConfirmDelete(null);
      }
    } catch (err) {
      console.error("Error deleting contact:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return contacts.filter(c => {
      const matchTab = activeTab === "all" || (
        activeTab === "Academic" ? c.Department.includes("Department") :
        activeTab === "Student Services" ? ["Admission Cell", "Student Welfare", "Placement Cell", "Library"].includes(c.Department) :
        activeTab === "IT & Infrastructure" ? ["IT Support", "Security", "Other"].includes(c.Department) :
        c.Department === activeTab
      );
      const matchSearch = !search || c.Full_Name.toLowerCase().includes(search.toLowerCase()) || c.Designation.toLowerCase().includes(search.toLowerCase()) || c.Department.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [contacts, activeTab, search]);

  const stats = useMemo(() => {
    return [
      { label: "Total Contacts", value: contacts.length, icon: "👥", color: "#6366f1" },
      { label: "Dept Heads",    value: contacts.filter(c => c.Department_Head).length, icon: "👑", color: "#22c55e" },
      { label: "Primary List",  value: contacts.filter(c => c.Primary_Contact).length, icon: "⭐", color: "#f59e0b" },
      { label: "Publicly Visible", value: contacts.filter(c => c.Show_on_Website).length, icon: "🌐", color: "#06b6d4" },
    ];
  }, [contacts]);

  const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "??";

  return (
    <div className="cd-page">
      {/* ── Loading Overlay ── */}
      {loading && (
        <div className="cd-loading-overlay">
          <div className="cd-spinner" />
          <p>Processing contact data...</p>
        </div>
      )}

      {/* ── Header ── */}
      <div className="cd-header">
        <div className="cd-header-left">
          <div className="cd-header-icon">📞</div>
          <div>
            <div className="cd-breadcrumb">
              <span>Media & Contact</span>
              <span>/</span>
              <span className="cd-bc-active">Contact Directory</span>
            </div>
            <h1>Contact Directory</h1>
            <p>Maintain the official office directory and emergency contact list for the college ecosystem.</p>
          </div>
        </div>
        <div className="cd-header-actions">
          <button className="cd-btn-primary" onClick={openAdd}>+ Add Contact</button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="cd-stats-row">
        {stats.map((s, i) => (
          <div key={i} className="cd-stat-card" style={{ borderTopColor: s.color }}>
            <span className="cd-stat-icon">{s.icon}</span>
            <div>
              <strong>{s.value}</strong>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs & Filters ── */}
      <div className="cd-tabs-wrap">
        <div className="cd-tabs">
          {TABS.map(t => (
            <button 
              key={t.key} 
              className={`cd-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
        
        <div className="cd-filters">
          <div className="cd-search">
            <span>🔍</span>
            <input 
              type="text" 
              placeholder="Search by name, role or office..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button className="cd-clr" onClick={() => setSearch("")}>×</button>}
          </div>
        </div>
      </div>

      {/* ── Content Grid ── */}
      <div className="cd-contacts-grid">
        {filtered.length > 0 ? (
          filtered.map(item => (
            <div key={item._id} className={`cd-contact-card ${item.Primary_Contact ? "primary-card" : ""} ${!item.Show_on_Website ? "hidden-card" : ""}`}>
              <div className="cd-card-top">
                <div className="cd-avatar" style={{ background: item.Primary_Contact ? "#fffbeb" : "#f1f5f9", color: item.Primary_Contact ? "#d97706" : "#475569" }}>
                  {getInitials(item.Full_Name)}
                </div>
                <div className="cd-card-badges">
                  {item.Department_Head && <span className="cd-head-badge">Dept Head</span>}
                  {item.Primary_Contact && <span className="cd-primary-badge">Primary</span>}
                  {!item.Show_on_Website && <span className="cd-hidden-badge">Internal Only</span>}
                </div>
                <button className="cd-dots" onClick={(e) => openDD(e, item._id)}>⋮</button>
              </div>

              <div className="cd-card-info">
                <h3 onClick={() => openView(item)}>{item.Full_Name}</h3>
                <p className="cd-designation">{item.Designation}</p>
                <span className="cd-dept-tag">🏛️ {item.Department}</span>
              </div>

              <div className="cd-card-details">
                <div className="cd-detail-row">
                  <span>📧</span> <a href={`mailto:${item.Email_Address}`}>{item.Email_Address || "N/A"}</a>
                </div>
                <div className="cd-detail-row">
                  <span>📞</span> <span>{item.Phone_Number || "N/A"} {item.Extension && <small className="cd-ext">Ext: {item.Extension}</small>}</span>
                </div>
                {item.Office_Location && (
                  <div className="cd-detail-row">
                    <span>📍</span> <span>{item.Office_Location}</span>
                  </div>
                )}
              </div>

              <div className="cd-card-footer">
                <span className={`cd-status-indicator ${item.Status.toLowerCase()}`}>
                  {item.Status}
                </span>
                <button className="cd-view-btn" onClick={() => openView(item)}>Full Profile →</button>
              </div>
            </div>
          ))
        ) : (
          <div className="cd-empty">
            <div>📇</div>
            <h3>No contacts found</h3>
            <p>Adjust your search or category filters to find what you're looking for.</p>
          </div>
        )}
      </div>

      {/* ── Dropdown Menu ── */}
      {dropdown.id && (
        <div ref={dropdownRef} className="cd-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left }}>
          <button onClick={() => openEdit(contacts.find(c => c._id === dropdown.id))}>✏️ Edit Contact</button>
          <button onClick={() => openView(contacts.find(c => c._id === dropdown.id))}>👁️ View Details</button>
          <div className="cd-dd-divider" />
          <button className="cd-dd-del" onClick={() => { setConfirmDelete(dropdown.id); closeDD(); }}>🗑️ Remove Forever</button>
        </div>
      )}

      {/* ── View Modal ── */}
      {modal === "view" && viewItem && (
        <div className="cd-overlay" onClick={() => setModal("")}>
          <div className="cd-view-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-view-head">
              <div className="cd-view-avatar">{getInitials(viewItem.Full_Name)}</div>
              <div>
                <h2>{viewItem.Full_Name}</h2>
                <p>{viewItem.Designation} • {viewItem.Department}</p>
              </div>
              <button className="cd-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <div className="cd-view-body">
              <div className="cd-view-grid">
                <div className="cd-view-item"><span>Email Address</span><strong>{viewItem.Email_Address || "N/A"}</strong></div>
                <div className="cd-view-item"><span>Contact Number</span><strong>{viewItem.Phone_Number || "N/A"}</strong></div>
                <div className="cd-view-item"><span>Extension</span><strong>{viewItem.Extension || "None"}</strong></div>
                <div className="cd-view-item"><span>Office Location</span><strong>{viewItem.Office_Location || "N/A"}</strong></div>
                <div className="cd-view-item"><span>Availability</span><strong>{viewItem.Availability || "Office Hours"}</strong></div>
                <div className="cd-view-item"><span>Status</span><strong>{viewItem.Status}</strong></div>
              </div>
              
              <div className="cd-view-notes">
                <h4>Admin Notes</h4>
                <p>{viewItem.Notes || "No internal notes provided for this contact."}</p>
              </div>

              <div className="cd-view-actions">
                <button className="cd-btn-secondary" onClick={() => openEdit(viewItem)}>Edit Profile</button>
                <button className="cd-btn-primary" onClick={() => setModal("")}>Close Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Form Modal (Add/Edit) ── */}
      {(modal === "add" || modal === "edit") && (
        <div className="cd-overlay" onClick={() => setModal("")}>
          <div className="cd-form-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-head">
              <div>
                <h2>{modal === "add" ? "Add New Contact" : "Edit Profile"}</h2>
                <p>Provide the official contact details for the college directory.</p>
              </div>
              <button className="cd-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <form className="cd-form" onSubmit={handleSave}>
              <div className="cd-field full">
                <label>Full Name <span className="req">*</span></label>
                <input 
                  type="text" 
                  required 
                  value={form.Full_Name} 
                  onChange={e => setForm({...form, Full_Name: e.target.value})}
                  placeholder="e.g. Dr. Ramesh Kumar"
                />
              </div>

              <div className="cd-field-row">
                <div className="cd-field">
                  <label>Designation <span className="req">*</span></label>
                  <input 
                    type="text" 
                    required 
                    value={form.Designation} 
                    onChange={e => setForm({...form, Designation: e.target.value})}
                    placeholder="e.g. Head of IT"
                  />
                </div>
                <div className="cd-field">
                  <label>Department <span className="req">*</span></label>
                  <select value={form.Department} onChange={e => setForm({...form, Department: e.target.value})}>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div className="cd-field-row">
                <div className="cd-field">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    value={form.Email_Address} 
                    onChange={e => setForm({...form, Email_Address: e.target.value})}
                    placeholder="official.email@rajatcollege.ac.in"
                  />
                </div>
                <div className="cd-field">
                  <label>Phone Number</label>
                  <input 
                    type="text" 
                    value={form.Phone_Number} 
                    onChange={e => setForm({...form, Phone_Number: e.target.value})}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div className="cd-field-row">
                <div className="cd-field">
                  <label>Extension</label>
                  <input 
                    type="text" 
                    value={form.Extension} 
                    onChange={e => setForm({...form, Extension: e.target.value})}
                  />
                </div>
                <div className="cd-field">
                  <label>Office Location</label>
                  <input 
                    type="text" 
                    value={form.Office_Location} 
                    onChange={e => setForm({...form, Office_Location: e.target.value})}
                    placeholder="e.g. Main Block, Room 104"
                  />
                </div>
              </div>

              <div className="cd-field-row">
                <div className="cd-field">
                  <label>Availability</label>
                  <input 
                    type="text" 
                    value={form.Availability} 
                    onChange={e => setForm({...form, Availability: e.target.value})}
                    placeholder="e.g. 10 AM - 5 PM"
                  />
                </div>
                <div className="cd-field">
                  <label>Status</label>
                  <select value={form.Status} onChange={e => setForm({...form, Status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Away">Away / On Leave</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="cd-field full">
                <label>Admin Notes</label>
                <textarea 
                  rows="2" 
                  value={form.Notes} 
                  onChange={e => setForm({...form, Notes: e.target.value})}
                  placeholder="Internal notes for administrators..."
                />
              </div>

              <div className="cd-toggle-grid">
                <label className="cd-toggle-item">
                  <input type="checkbox" checked={form.Show_on_Website} onChange={e => setForm({...form, Show_on_Website: e.target.checked})} />
                  <div className={`cd-chk-box ${form.Show_on_Website ? "checked" : ""}`} />
                  <div><strong>Show on Website</strong><p>Visible to students and public.</p></div>
                </label>
                <label className="cd-toggle-item">
                  <input type="checkbox" checked={form.Department_Head} onChange={e => setForm({...form, Department_Head: e.target.checked})} />
                  <div className={`cd-chk-box ${form.Department_Head ? "checked" : ""}`} />
                  <div><strong>Department Head</strong><p>Highlight as a decision maker.</p></div>
                </label>
                <label className="cd-toggle-item">
                  <input type="checkbox" checked={form.Primary_Contact} onChange={e => setForm({...form, Primary_Contact: e.target.checked})} />
                  <div className={`cd-chk-box ${form.Primary_Contact ? "checked" : ""}`} />
                  <div><strong>Primary Contact</strong><p>Featured emergency contact.</p></div>
                </label>
              </div>

              <div className="cd-form-footer">
                <button type="button" className="cd-btn-light" onClick={() => setModal("")}>Cancel</button>
                <button type="submit" className="cd-btn-primary">Save Contact Profile</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ── */}
      {confirmDelete && (
        <div className="cd-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="cd-confirm" onClick={e => e.stopPropagation()}>
            <div className="cd-confirm-icon">⚠️</div>
            <h3>Remove Contact?</h3>
            <p>This will permanently delete the contact profile from the directory.</p>
            <div className="cd-confirm-actions">
              <button className="cd-btn-light" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="cd-btn-danger" onClick={() => handleDelete(confirmDelete)}>Remove Forever</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDirectory;